import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { AppConfigStore } from '../app-config/app-config.store';

/**
 * Ensures CMS config (background images, etc.) is loaded — or has
 * definitively failed/timed out — before any route that renders
 * `<app-cms-background>` activates.
 *
 * Root fix for a real UX bug: every `CmsBackgroundComponent` shows its local
 * bundled fallback image synchronously on `ngOnInit()`, then swaps to the
 * real CMS-provided background once `AppConfigStore.ensureLoaded()`
 * resolves. On a cold page load that fetch hasn't even started by the time
 * the first screen (typically Splash) mounts, so users reliably see the
 * fallback (e.g. `jio-pc-banner.png`) flash before the real background swaps
 * in. Source comparison confirmed Consumer has the exact same race — no
 * prefetch, no `initialData`, no loading gate around it — so this isn't an
 * Angular-specific regression, but the goal here is to actually fix the
 * flash rather than faithfully reproduce it.
 *
 * Blocking route activation on this guard means the app's existing
 * navigation-loading spinner (see `app.ts`'s `trackRouteLoading()`) covers
 * the small extra wait instead of a spinner -> wrong-image -> right-image
 * sequence — reusing UX that's already in place rather than inventing a new
 * one. `AppConfigStore.ensureLoaded()` is itself de-duped (single in-flight
 * request, 10-minute staleTime cache — see `AppConfigStore.peekFresh()`), so
 * this only actually waits on the very first navigation of a session (or
 * once the cache goes stale); every other navigation resolves synchronously.
 *
 * NOTE: this guard's bounded wait is NOT what guarantees the CMS-sourced
 * `clientApiKey`/`analyticsAPIKey` are ready before a request needs them —
 * timing out here must never mean a request goes out with an empty
 * `x-api-key`. That guarantee lives in `auth.interceptor.ts` and
 * `token-refresh.service.ts` themselves, which each call the same
 * `AppConfigStore.waitReady()` (de-duped, so no extra fetch) and refuse to
 * proceed if the key is still missing after it resolves. This guard existing
 * or timing out has no bearing on that.
 */
export const appConfigReadyGuard: CanActivateFn = async () => {
  const appConfigStore = inject(AppConfigStore);
  // Render-only wait: a config restored from the last launch is enough to pick the
  // background, so a cold start doesn't hold the first screen on the CDN.
  await appConfigStore.waitReady(undefined, true);
  return true;
};
